// Repeat-guest memory for the reservation form. Persists the contact block in
// localStorage so a returning guest doesn't retype it. Best-effort only: every
// storage access is guarded for SSR and wrapped in try/catch, and the shape is
// validated on load so a stale or corrupt entry never breaks the form.

const STORAGE_KEY = "tuscan_reservation_guest";

export type GuestMemory = {
  name: string;
  phone: string;
  email: string;
  smsConsent: boolean;
  savedAt: number;
};

export function loadGuestMemory(): GuestMemory | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    const { name, phone, email, smsConsent, savedAt } = parsed as Record<string, unknown>;
    if (typeof name !== "string" || typeof phone !== "string" || typeof email !== "string") {
      return null;
    }

    return {
      name,
      phone,
      email,
      smsConsent: smsConsent === true,
      savedAt: typeof savedAt === "number" ? savedAt : 0,
    };
  } catch {
    return null;
  }
}

export function saveGuestMemory(memory: Omit<GuestMemory, "savedAt">): void {
  if (typeof window === "undefined") return;
  try {
    const payload: GuestMemory = { ...memory, savedAt: Date.now() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage unavailable (private mode, quota). Memory is a convenience only.
  }
}

export function clearGuestMemory(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage errors; nothing depends on a successful clear.
  }
}
